import OptionsService from '@/api/services/options.service';
import { optionUsage } from '@/domain/catalog/option-groups';
import type { TAssistantOpName } from '@/interfaces/assistant.interface';
import type { ICategory } from '@/interfaces/ICategory';
import type { IItem } from '@/interfaces/IItem';
import {
  allergenSlugs,
  buildEntitiesById,
  categoryToDetails,
  itemToDetails,
  itemVat,
} from '../../menu/gpthelp-admin-menu-local';
import {
  filterOptionGroups,
  fitGroupReads,
  libraryGroups,
  OPTION_GROUPS_DETAIL_MAX,
  OPTION_GROUPS_LIST_SAMPLE,
  OPTION_GROUPS_MAX_RESULT_CHARS,
  optionGroupRead,
} from '../../menu/gpthelp-admin-option-groups';
import type { TGpthelpLibraryGroup } from '../../menu/gpthelp-admin-option-groups';
import { omitNullish } from '../sanitize';
import { runToolApiCall } from '../tool-http';
import {
  resolveCategoryVisibility,
  resolveItemVisibility,
  type IGpthelpResolvedHiddenReason,
} from '../../menu/gpthelp-admin-menu-visibility';
import { computeItemPrices } from '../../menu/gpthelp-admin-menu-pricing';
import {
  GPTHELP_CATALOG_HEALTH_FILTERS,
  resolveOfferedMenus,
  searchCatalogItems,
  type IGpthelpCatalogMenuFilter,
  type IGpthelpCatalogSearchData,
} from '../../menu/gpthelp-admin-catalog';
import {
  buildGpthelpTaxCtx,
  buildGpthelpVisibilityCtx,
} from '../../gpthelp-host-ctx-builders';
import {
  HostToolError,
  type ICartadigitalAdminHostToolDeps,
  type IGpthelpTool,
  type IGpthelpToolGate,
} from '../tool-types';
import { kindOps, proposalToolOffered } from '../proposal-kinds';
import {
  clampInt,
  getOptionalBoolean,
  getOptionalEnum,
  getOptionalNumber,
  getOptionalNumberArray,
  getOptionalString,
  getOptionalVisibilityFilter,
} from '../tool-args';

/** Products per menu_getCategory call. */
const CATEGORY_PAGE_MAX = 50;

/** menu_getOptionGroups: groups per page, and groups asked for by id. */
const OPTION_GROUPS_PAGE_MAX = 50;
const OPTION_GROUPS_PAGE_DEFAULT = 20;
const OPTION_GROUP_IDS_MAX = 20;

/** The ids of `key` as whole numbers, each once, in the order given; null when absent. */
function idListArg(
  args: Record<string, unknown>,
  key: string,
  max: number
): number[] | null {
  const ids = getOptionalNumberArray(args, key, max);
  return ids === null ? null : [...new Set(ids.map((id) => Math.floor(id)))];
}

/**
 * `value` as a UTC instant («…Z»), which the registry localizes to the
 * branch time zone; null when it is not a date.
 */
function utcInstantOf(value: Date | string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/**
 * Menu/visibility helpers shared by the menu tools and the stats tools'
 * visibility enrichment. Built per execute call over the current stores.
 */
export function buildMenuToolHelpers(deps: ICartadigitalAdminHostToolDeps) {
  const getMenuCatalogSnapshotOrThrow = (): {
    categories: ICategory[];
    items: IItem[];
    categoriesById: Record<number, ICategory>;
    itemsById: Record<number, IItem>;
  } => {
    const categories = deps.menuStore.categories;
    const items = deps.menuStore.items;

    if (!Array.isArray(categories) || !Array.isArray(items)) {
      throw new HostToolError(
        'CATALOG_NOT_LOADED',
        'Menú no cargado todavía. Esperá unos segundos o recargá la página.',
      );
    }

    return {
      categories,
      items,
      categoriesById: buildEntitiesById(categories),
      itemsById: buildEntitiesById(items),
    };
  };

  const visibilityCtx = buildGpthelpVisibilityCtx({ restaurantStore: deps.restaurantStore });
  const taxCtx = buildGpthelpTaxCtx({ restaurantStore: deps.restaurantStore });
  const offeredMenus = resolveOfferedMenus(deps.menuStore.availableMenus);
  const offeredMenuIds = new Set<number | null>(offeredMenus.map((m) => m.id ?? null));

  const resolveHiddenInBranches = (
    hiddenBranchesIds: string[] | null | undefined,
  ): Array<{ id: number; name: string }> => {
    if (!Array.isArray(hiddenBranchesIds) || hiddenBranchesIds.length === 0) return [];

    const branches = deps.restaurantStore.branches ?? [];
    const hiddenIdSet = new Set(hiddenBranchesIds);

    return branches
      .filter((b) => hiddenIdSet.has(String(b.id)))
      .map((b) => ({ id: b.id, name: b.name ?? '' }));
  };

  // Adds hiddenNow/notInCatalog to stats entries. Skips entirely (emits nothing)
  // when the catalog is not loaded — never throws from enrichment.
  const enrichStatsEntryWithVisibility = (
    entry: { notInCatalog?: true; hiddenNow?: IGpthelpResolvedHiddenReason[] },
    itemId: number,
  ): void => {
    try {
      const itemMap = deps.menuStore.itemMap;
      const categoryMap = deps.menuStore.categoryMap;
      if (!itemMap || !categoryMap) return;

      const item = itemMap[itemId];
      if (!item) {
        entry.notInCatalog = true;
        return;
      }

      const category = categoryMap[item.categoryId] ?? null;
      const menuOffered = category !== null && offeredMenuIds.has(category.menuId ?? null);
      const visibility = resolveItemVisibility({
        item,
        category,
        menuOffered,
        ctx: visibilityCtx,
      });

      if (!visibility.customerVisibleNow) {
        entry.hiddenNow = visibility.hiddenReasons;
      }
    } catch {
      // Enrichment is best-effort; never fail the stats tool because of it.
    }
  };

  return {
    getMenuCatalogSnapshotOrThrow,
    visibilityCtx,
    taxCtx,
    offeredMenus,
    offeredMenuIds,
    resolveHiddenInBranches,
    enrichStatsEntryWithVisibility,
  };
}

/** menu_getOptionGroups' description (plan P1-4) up to its last sentence's end. */
const OPTION_GROUPS_DESCRIPTION =
  "The store's option groups (the Options page: extras, sauces, flavours, choices; product sizes are not here: they are in each product's details): each group's rule, its choices (paused ones included, with ids and prices) and the products that use it. Without groupIds: a list (filter with query, itemId or unused). With groupIds: those groups in full. Groups are shared: a change to one shows in every product listed";

/**
 * How the description's last sentence ends while the member can prepare a
 * change to a shared group (plan P1-4; W12a decision: Phase 3 adds it back).
 * Names no tool or op (plan F10).
 */
const OPTION_GROUPS_SHARED_END =
  '; the proposal card lists them and states the count, so never stop to confirm it.';

/**
 * The ops that change a group every product using it shows (plan P3-1: its
 * name, description, rules and choices): the card of such a change lists
 * those products (plan P3-4, D3).
 */
const SHARED_GROUP_OPS: readonly TAssistantOpName[] = [
  'updateOptionGroup',
  'addOptionChoices',
  'updateOptionChoice',
  'removeOptionChoices',
  'reorderOptionChoices',
];

/** The member is offered menu_proposeChanges with a change to a shared group. */
function sharedGroupChangesOffered(gate: IGpthelpToolGate): boolean {
  return (
    proposalToolOffered('menu.changes', gate) &&
    kindOps('menu.changes', gate).some((op) => SHARED_GROUP_OPS.includes(op))
  );
}

const optionGroupsDefinition: IGpthelpTool['definition'] = {
  name: 'menu_getOptionGroups',
  description: `${OPTION_GROUPS_DESCRIPTION}.`,
  sideEffects: 'none',
  parameters: {
    type: 'object',
    additionalProperties: false,
    properties: {
      groupIds: {
        type: 'array',
        items: { type: 'integer' },
        maxItems: OPTION_GROUP_IDS_MAX,
        description:
          'Those groups in full, with every choice and every product that uses them.',
      },
      query: {
        type: 'string',
        description:
          'Words that must all appear in the group name or one of its choices (menu language; case and accents ignored).',
      },
      itemId: {
        type: 'integer',
        description: 'Only the groups this product uses.',
      },
      unused: {
        type: 'boolean',
        description: 'Only groups no product uses.',
      },
      limit: {
        type: 'integer',
        minimum: 1,
        maximum: OPTION_GROUPS_PAGE_MAX,
        default: OPTION_GROUPS_PAGE_DEFAULT,
      },
      offset: { type: 'integer', minimum: 0, default: 0 },
    },
  },
};

export const menuTools: IGpthelpTool[] = [
  {
    definition: {
      name: 'menu_getItem',
      description:
        'Get full details of ANY menu item by id, locally (no API) - including items customers cannot currently see. Includes options, prices as shown to customers, schedule, per-branch hiding and an authoritative visibility evaluation. options[].id is the optionGroupId and options[].optionItems[].id the choiceId; main:true holds the sizes (their ids are sizeId). active:false = paused or hidden on this product. usedByCount = how many products share the group. discountedPrice = what customers pay when set. lastPriceChangeAt = when its price last changed (absent: never recorded).',
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          itemId: { type: 'number' },
        },
        required: ['itemId'],
      },
    },
    policy: 'read',
    isAvailable: (gate) => gate.hasRestaurantScope,
    execute(args, { deps }) {
      const helpers = buildMenuToolHelpers(deps);

      const itemIdRaw = getOptionalNumber(args, 'itemId');
      const itemId = typeof itemIdRaw === 'number' ? Math.floor(itemIdRaw) : NaN;
      if (!Number.isFinite(itemId) || itemId <= 0) {
        throw new HostToolError('INVALID_ARGS', 'Invalid "itemId"', { itemId: args.itemId });
      }

      const snapshot = helpers.getMenuCatalogSnapshotOrThrow();
      const item = snapshot.itemsById[itemId] ?? null;
      if (!item) {
        throw new HostToolError('NOT_FOUND', 'Item not found', { itemId });
      }

      const category = snapshot.categoriesById[item.categoryId] ?? null;
      const menuOffered =
        category !== null && helpers.offeredMenuIds.has(category.menuId ?? null);
      const visibility = resolveItemVisibility({
        item,
        category,
        menuOffered,
        ctx: helpers.visibilityCtx,
      });
      const prices = computeItemPrices(item, helpers.taxCtx);

      return {
        item: omitNullish({
          ...itemToDetails({
            item,
            categoryName: category?.name ?? null,
            usage: optionUsage(snapshot.items),
          }),
          // Read with the store's private fields (plan P1-5); never recorded = absent.
          lastPriceChangeAt: utcInstantOf(item.lastPriceChangeAt),
          allergens: allergenSlugs(item.allergens),
          vat: itemVat(item.vat),
        }),
        prices: {
          base: prices.base,
          discounted: prices.discounted,
          finalOnCustomerMenu: prices.final,
          finalBeforeDiscount: prices.finalBeforeDiscount,
        },
        visibility: {
          customerVisibleNow: visibility.customerVisibleNow,
          hiddenReasons: visibility.hiddenReasons,
          checkedAt: new Date().toISOString(),
        },
        hiddenInBranches: helpers.resolveHiddenInBranches(item.hiddenBranchesIds),
      };
    },
  },
  {
    definition: {
      name: 'menu_findItems',
      description:
        "Search or list menu items across the WHOLE catalog (all menus, hidden items included). Every word of query must appear in the product's name, description or category name (case and accents ignored), so a category name lists its products. Write the query in the MENU's language (translate the user's words, e.g. 咖啡 → café) and in singular. Use before ever claiming an item does not exist. Also resolves itemIds from stats results. visibility='customer_visible_now' answers 'what do customers see right now'.",
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          query: { type: 'string' },
          itemIds: {
            type: 'array',
            items: { type: 'number' },
            maxItems: 50,
          },
          categoryId: { type: 'number' },
          menuId: {
            type: 'number',
            description: 'Filter by menu id (non-default menus only).',
          },
          defaultMenuOnly: {
            type: 'boolean',
            default: false,
            description: 'Only items of the default menu.',
          },
          visibility: {
            type: 'string',
            enum: [
              'any',
              'customer_visible_now',
              'hidden_any_reason',
              'disabled',
              'hidden_in_branch',
              'outside_hours_now',
            ],
            default: 'any',
          },
          discountedOnly: { type: 'boolean', default: false },
          health: {
            type: 'string',
            enum: [...GPTHELP_CATALOG_HEALTH_FILTERS],
            description:
              "The Menú page's health filters: products with no photo, no description, no price (0; Point-of-Sale-only categories excluded), with their own or their category's schedule, or with a discount.",
          },
          limit: { type: 'number', minimum: 1, maximum: 50, default: 20 },
          offset: { type: 'number', minimum: 0, default: 0 },
        },
      },
    },
    policy: 'read',
    isAvailable: (gate) => gate.hasRestaurantScope,
    execute(args, { deps }) {
      const helpers = buildMenuToolHelpers(deps);

      const query = getOptionalString(args, 'query');
      const itemIds = getOptionalNumberArray(args, 'itemIds', 50);
      const categoryId = getOptionalNumber(args, 'categoryId');
      const menuId = getOptionalNumber(args, 'menuId');
      const defaultMenuOnly = getOptionalBoolean(args, 'defaultMenuOnly') ?? false;
      const visibility = getOptionalVisibilityFilter(args, 'visibility') ?? 'any';
      const discountedOnly = getOptionalBoolean(args, 'discountedOnly') ?? false;
      const health = getOptionalEnum(
        args,
        'health',
        GPTHELP_CATALOG_HEALTH_FILTERS
      );
      const limit = clampInt(getOptionalNumber(args, 'limit') ?? 20, 1, 50);
      const offset = clampInt(
        getOptionalNumber(args, 'offset') ?? 0,
        0,
        Number.MAX_SAFE_INTEGER,
      );

      if (menuId !== null && defaultMenuOnly) {
        throw new HostToolError(
          'INVALID_ARGS',
          'Provide either "menuId" or "defaultMenuOnly", not both',
          { menuId, defaultMenuOnly },
        );
      }

      let menuFilter: IGpthelpCatalogMenuFilter = null;
      if (menuId !== null) menuFilter = { menuId: Math.floor(menuId) };
      else if (defaultMenuOnly) menuFilter = { defaultMenu: true };

      const snapshot = helpers.getMenuCatalogSnapshotOrThrow();
      const searchData: IGpthelpCatalogSearchData = {
        categories: snapshot.categories,
        items: snapshot.items,
        availableMenus: deps.menuStore.availableMenus,
        ctx: helpers.visibilityCtx,
        taxCtx: helpers.taxCtx,
      };

      return searchCatalogItems(
        {
          query,
          itemIds,
          categoryId: categoryId !== null ? Math.floor(categoryId) : null,
          menuFilter,
          visibility,
          discountedOnly,
          health,
          limit,
          offset,
        },
        searchData,
      );
    },
  },
  {
    definition: {
      name: 'menu_getCategory',
      description:
        'Get full details of any category by id, locally (no API) - including hidden/pos-only categories: schedule, per-branch hiding, and all its items as summaries with visibility flags, evaluated at call time. Lists up to 50 products per call: page with offset = nextOffset.',
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          categoryId: { type: 'number' },
          offset: { type: 'integer', minimum: 0, default: 0 },
          limit: {
            type: 'integer',
            minimum: 1,
            maximum: CATEGORY_PAGE_MAX,
            default: CATEGORY_PAGE_MAX,
          },
        },
        required: ['categoryId'],
      },
    },
    policy: 'read',
    isAvailable: (gate) => gate.hasRestaurantScope,
    execute(args, { deps }) {
      const helpers = buildMenuToolHelpers(deps);

      const categoryIdRaw = getOptionalNumber(args, 'categoryId');
      const categoryId =
        typeof categoryIdRaw === 'number' ? Math.floor(categoryIdRaw) : NaN;
      if (!Number.isFinite(categoryId) || categoryId <= 0) {
        throw new HostToolError('INVALID_ARGS', 'Invalid "categoryId"', {
          categoryId: args.categoryId,
        });
      }

      const limit = clampInt(
        getOptionalNumber(args, 'limit') ?? CATEGORY_PAGE_MAX,
        1,
        CATEGORY_PAGE_MAX,
      );
      const offset = clampInt(
        getOptionalNumber(args, 'offset') ?? 0,
        0,
        Number.MAX_SAFE_INTEGER,
      );

      const snapshot = helpers.getMenuCatalogSnapshotOrThrow();
      const category = snapshot.categoriesById[categoryId] ?? null;
      if (!category) {
        throw new HostToolError('NOT_FOUND', 'Category not found', { categoryId });
      }

      const categoryMenuId = category.menuId ?? null;
      const offeredMenu =
        helpers.offeredMenus.find((m) => (m.id ?? null) === categoryMenuId) ?? null;
      const menuOffered = offeredMenu !== null;
      const visibility = resolveCategoryVisibility({
        category,
        menuOffered,
        ctx: helpers.visibilityCtx,
      });

      const searchData: IGpthelpCatalogSearchData = {
        categories: snapshot.categories,
        items: snapshot.items,
        availableMenus: deps.menuStore.availableMenus,
        ctx: helpers.visibilityCtx,
        taxCtx: helpers.taxCtx,
      };

      const itemsPage = searchCatalogItems({ categoryId, limit, offset }, searchData);
      const pageEnd = itemsPage.offset + itemsPage.results.length;
      const customerVisiblePage = searchCatalogItems(
        { categoryId, visibility: 'customer_visible_now', limit: 1, offset: 0 },
        searchData,
      );

      return {
        category: categoryToDetails({ category }),
        visibility: {
          customerVisibleNow: visibility.customerVisibleNow,
          hiddenReasons: visibility.hiddenReasons,
          checkedAt: new Date().toISOString(),
        },
        hiddenInBranches: helpers.resolveHiddenInBranches(category.hiddenBranchesIds),
        menu: {
          id: categoryMenuId,
          title: offeredMenu?.title ?? null,
          orderTypes: offeredMenu?.types ?? [],
          offered: menuOffered,
        },
        counts: {
          items: itemsPage.total,
          customerVisibleNow: customerVisiblePage.total,
        },
        totalItems: itemsPage.total,
        offset: itemsPage.offset,
        limit: itemsPage.limit,
        nextOffset: pageEnd < itemsPage.total ? pageEnd : null,
        items: itemsPage.results,
      };
    },
  },
  {
    definition: optionGroupsDefinition,
    // The card names the products and their count: said only while the
    // member can prepare such a change (plan F10).
    definitionFor: (gate) =>
      sharedGroupChangesOffered(gate)
        ? {
            ...optionGroupsDefinition,
            description: `${OPTION_GROUPS_DESCRIPTION}${OPTION_GROUPS_SHARED_END}`,
          }
        : optionGroupsDefinition,
    policy: 'read',
    // The Opciones page's API gate: CatalogManage (plan P1-4).
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canManageCatalog,
    async execute(args, { deps, scope }) {
      const groupIds = idListArg(args, 'groupIds', OPTION_GROUP_IDS_MAX);
      const query = getOptionalString(args, 'query');
      const itemIdRaw = getOptionalNumber(args, 'itemId');
      const itemId = itemIdRaw === null ? null : Math.floor(itemIdRaw);
      if (itemId !== null && itemId <= 0) {
        throw new HostToolError('INVALID_ARGS', 'Invalid "itemId"', {
          itemId: args.itemId,
        });
      }
      const unused = getOptionalBoolean(args, 'unused') ?? false;
      const limit = clampInt(
        getOptionalNumber(args, 'limit') ?? OPTION_GROUPS_PAGE_DEFAULT,
        1,
        OPTION_GROUPS_PAGE_MAX
      );
      const offset = clampInt(
        getOptionalNumber(args, 'offset') ?? 0,
        0,
        Number.MAX_SAFE_INTEGER
      );

      // One read per call: every menu, paused choices, live links.
      const response = await runToolApiCall(() =>
        OptionsService.getAllUsable(
          scope.restaurantId,
          scope.branchId,
          'all',
          'all'
        )
      );
      const groups = libraryGroups(Array.isArray(response) ? response : []);
      const categoryNameOf = (categoryId: number): string | null =>
        deps.menuStore.categoryMap?.[categoryId]?.name ?? null;
      // The widget refuses a tool result over 60,000 chars: the groups that
      // do not fit wait for the next call.
      const fit = (selected: readonly TGpthelpLibraryGroup[], max: number) =>
        fitGroupReads(selected, {
          max,
          maxChars: OPTION_GROUPS_MAX_RESULT_CHARS,
          read: (group, cap) =>
            omitNullish({
              ...optionGroupRead(group, { max: cap, categoryNameOf }),
            }),
        });

      if (groupIds !== null) {
        const byId = new Map(groups.map((group) => [group.id, group]));
        const missing = groupIds.filter((id) => !byId.has(id));
        const { reads, left } = fit(
          groupIds.flatMap((id) => {
            const group = byId.get(id);
            return group ? [group] : [];
          }),
          OPTION_GROUPS_DETAIL_MAX
        );
        return {
          groups: reads,
          ...(missing.length > 0 ? { missing } : {}),
          ...(left.length > 0
            ? { remainingGroupIds: left.map((group) => group.id) }
            : {}),
        };
      }

      const matches = filterOptionGroups(groups, { query, itemId, unused });
      // A page ends early when the next group would not fit: nextOffset
      // resumes right after the last group shown.
      const { reads } = fit(
        matches.slice(offset, offset + limit),
        OPTION_GROUPS_LIST_SAMPLE
      );
      const pageEnd = offset + reads.length;
      return {
        total: matches.length,
        offset,
        limit: reads.length,
        nextOffset: pageEnd < matches.length ? pageEnd : null,
        groups: reads,
      };
    },
  },
];
